<template>
    <button
        class="Button"
        tabindex="0"
        :disabled="isDisabled"
        :data-testid="testId"
        :class="[
            `is-${variant}`,
            { 'is-disabled': isDisabled },
            { 'has-button': hasIcon },
            { 'is-large': size === 'large' },
            { 'is-small': size === 'small' },
            { 'is-loading': isLoading },
            { [`is-${color}`]: color },
        ]"
        :style="style"
        @click="handleClick"
    >
        <div class="Button-Wrapper">
            <Spinner v-if="isLoading" />
            <span v-if="hasIcon || icon" class="IconBefore" ref="iconBefore">
                <slot name="iconBefore">
                    <i v-if="icon" :class="`ri-${icon}-line`"></i>
                </slot>
            </span>
            <span class="Label">
                <span v-if="value">{{ value }}</span>
                <slot></slot>
            </span>
        </div>
    </button>
</template>

<script>
import Spinner from '@/components/widgets/Spinner';

export default {
    name: 'Button',
    components: {
        Spinner,
    },
    props: {
        onClick: {
            type: Function,
            default: () => {},
        },
        isLoading: Boolean,
        size: {
            type: String,
        },
        keyName: {
            type: String,
            default: null,
        },
        isDisabled: {
            type: Boolean,
            default: false,
        },
        value: {
            type: String,
            default: '',
        },
        marginBottom: {
            type: Number,
        },
        marginTop: {
            type: Number,
        },
        marginLeft: {
            type: Number,
        },
        marginRight: {
            type: Number,
        },
        variant: {
            type: String,
            default: '',
        },
        color: {
            type: String,
            default: '',
        },
        icon: {
            type: String,
            default: '',
        },
        testId: {
            type: String,
            default: null,
        },
    },
    computed: {
        hasIcon() {
            return !!this.$slots.iconBefore || !!this.icon;
        },
        style() {
            const margins = ['Top', 'Bottom', 'Left', 'Right'];
            return margins.reduce((acc, direction) => {
                const propName = `margin${direction}`;
                if (this[propName] !== undefined) {
                    acc[propName] = `${this[propName] * 5}px`;
                }
                return acc;
            }, {});
        },
    },
    methods: {
        handleClick() {
            this.onClick();
            this.$emit('onClick');
        },
    },
};
</script>

<style lang="scss">
.theme-dark {
    .Button {
        border: solid 0.5px var(--color-text-inactive);
        box-shadow: 0 0 0 0 rgba(rgb(219, 218, 218), 1);

        &.is-minimal {
            &:hover {
                color: var(--color-text-black);
                border: solid 1px var(--color-border);
                box-shadow: 0 0 0 4px rgba(rgb(219, 218, 218), 1);
                background-color: #1a1a1a;
            }
        }

        &.is-plain {
            &:hover {
                color: var(--color-text-black);
                border: solid 1px var(--color-border);
                box-shadow: 0 0 0 4px rgba(rgb(219, 218, 218), 1);
                background-color: #050505;
            }
        }
    }
}

.Button {
    font-family: inherit;
    color: #fff;
    font-weight: 400;
    cursor: pointer;
    display: inline-block;
    user-select: none;
    border-radius: 5px;
    box-shadow: 0 0 0 4px rgba(var(--color-blue-dark), 0);
    background-color: var(--color-blue-dark);
    transition: all 0.3s ease;
    padding: 8px 18px;
    border: solid 2px transparent;
    outline: 0.5px solid transparent;
    outline-offset: 2px;

    @media (max-width: $breakpoint-tablet-landscape) {
        padding: 6px 14px;
    }

    &:hover {
        background-color: rgba(var(--color-blue-light-rgb), 1);
        box-shadow: 0 0 0 4px rgba(var(--color-blue-light-rgb), 0.2);
    }

    &:focus,
    &:focus-visible {
        box-shadow: 0 0 0 4px rgba(var(--color-blue-rgb), 0.3);
        outline: 0.5px solid var(--color-blue);
    }

    &.is-disabled {
        cursor: not-allowed;
        background-color: var(--color-text-gray-dark);
        box-shadow: 0 0 0 0px rgba(var(--color-grey), 1);
    }

    &.is-large {
        padding: 14px 28px 14px;

        .Label {
            font-size: 1.25rem;
        }
    }

    &.is-small {
        padding: 6px 14px;

        .Label {
            font-size: 0.75rem;
        }
    }

    &.is-basic {
        background-color: #e3e2e2;
        color: black;
        box-shadow: 0 0 0 0 rgba(var(--color-grey), 1);

        &:hover {
            box-shadow: 0 0 0 4px rgba(var(--color-grey), 1);
            background-color: var(--color-grey);
        }

        &:focus {
            box-shadow: 0 0 0 4px rgba(var(--color-grey), 1);
            outline: solid 1px var(--color-text-inactive);
        }
    }

    &.is-icon {
        color: var(--color-text-blue);
        background-color: transparent;
        border: solid 1px var(--color-border);
        box-shadow: 0 0 0 0 rgba(rgb(219, 218, 218), 1);

        padding: 6px;

        i {
            font-size: 0.95rem;
            font-weight: normal;
        }

        &.is-large {
            padding: 14px;

            i {
                font-size: 1.25rem;
            }
        }

        &.is-small {
            padding: 6px;

            i {
                font-size: 1.05rem;
            }
        }

        &:hover {
            color: var(--color-text-black);
            border: solid 1px var(--color-border);
            box-shadow: 0 0 0 4px rgba(rgb(219, 218, 218), 1);
            background-color: #e6e6e6;
        }

        &:focus {
            box-shadow: 0 0 0 4px rgba(var(--color-blue-light-rgb), 0.2);
            outline: solid 1px var(--color-text-inactive);
        }
    }

    &.is-plain {
        color: var(--color-text-black);
        background-color: transparent;
        border: solid 0.5px var(--color-border);
        box-shadow: 0 0 0 0 rgba(rgb(219, 218, 218), 1);

        &:hover {
            color: var(--color-text-black);
            border: solid 0.5px var(--color-border);
            box-shadow: 0 0 0 4px rgba(rgb(219, 218, 218), 1);
            background-color: #e6e6e6;
        }

        &:focus {
            box-shadow: 0 0 0 4px rgba(rgb(219, 218, 218), 1);
            outline: solid 1px var(--color-text-inactive);
        }
    }

    &.is-outline {
        color: var(--color-text-black);
        background-color: transparent;
        border: solid 1px var(--color-border);
        box-shadow: 0 0 0 0 rgba(rgb(219, 218, 218), 1);

        &:hover {
            color: var(--color-text-black);
            border: solid 1px var(--color-border);
            box-shadow: 0 0 0 4px rgba(rgb(219, 218, 218), 1);
            background-color: #e6e6e6;
        }

        &:focus {
            box-shadow: 0 0 0 4px rgba(rgb(219, 218, 218), 1);
            outline: solid 1px var(--color-text-inactive);
        }
    }

    &.is-minimal {
        color: var(--color-text-black);
        background-color: var(--color-white);
        border: solid 1px var(--color-border);
        box-shadow: 0 0 0 0px rgba(rgb(219, 218, 218), 1);

        &:hover {
            color: var(--color-text-black);
            border: solid 1px var(--color-border);
            box-shadow: 0 0 0 4px rgba(rgb(219, 218, 218), 1);
            background-color: #ffffff;
        }

        &:focus {
            box-shadow: 0 0 0 4px rgba(rgb(219, 218, 218), 1);
            outline: solid 1px var(--color-text-inactive);
        }
    }

    &.is-warning {
        background-color: var(--color-yellow-end);
        box-shadow: 0 0 0 0px rgba(var(--color-yellow-end), 1);
        color: black;

        &:hover {
            box-shadow: 0 0 0 4px rgba(var(--color-yellow-end), 1);
            background-color: #ffb366;
        }

        &:focus {
            box-shadow: 0 0 0 4px rgba(var(--color-yellow-end), 1);
            outline: solid 1px var(--color-text-inactive);
        }
    }

    &.is-success {
        background-color: var(--color-green-dark);
        color: white;
        box-shadow: 0 0 0 0px rgba(var(--color-green-dark-rgb), 1);

        &:hover {
            background-color: #3aa687;
            box-shadow: 0 0 0 4px rgba(var(--color-green-dark-rgb), 0.3);
        }

        &:focus {
            box-shadow: 0 0 0 4px rgba(var(--color-green-dark-rgb), 0.3);
            outline: solid 1px var(--color-text-inactive);
        }
    }

    &.is-error {
        background-color: var(--color-red);
        color: white;
        box-shadow: 0 0 0 0px rgba(var(--color-red-rgb), 1);

        &:hover {
            box-shadow: 0 0 0 4px rgba(var(--color-red-rgb), 1);
            background-color: #9b2e3a;
        }

        &:focus {
            box-shadow: 0 0 0 4px rgba(var(--color-red-rgb), 1);
            outline: solid 1px var(--color-text-inactive);
        }
    }

    &.is-red {
        background-color: var(--color-red);
        color: white;
        box-shadow: 0 0 0 0px rgba(var(--color-red-rgb), 1);

        &:hover {
            box-shadow: 0 0 0 4px rgba(var(--color-red-rgb), 1);
            background-color: #9b2e3a;
        }

        &:focus {
            box-shadow: 0 0 0 4px rgba(var(--color-red-rgb), 1);
            outline: solid 1px var(--color-text-inactive);
        }
    }

    &.is-table {
        background: none;
        border: solid 1px rgba(0, 0, 0, 0.2);
        color: var(--color-blue);
        font-size: 0.85rem;
        padding: 8px 12px;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: none;

        &:hover {
            background-color: rgba(0, 0, 0, 0.1);
            box-shadow: none;
        }

        &:active {
            transform: scale(0.97);
        }

        &:focus {
            box-shadow: 0 0 0 2px rgba(rgb(219, 218, 218), 0.5);
            outline: none;
        }

        &.is-small {
            padding: 6px 10px;
            font-size: 0.75rem;
        }
    }

    &.is-table-primary {
        background: none;
        border: solid 1px var(--color-blue);
        border-left: solid 4px var(--color-blue);
        color: var(--color-blue);
        font-size: 0.85rem;
        padding: 8px 12px;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: none;

        &:hover {
            background-color: rgba(0, 0, 0, 0.1);
            box-shadow: none;
        }

        &:active {
            transform: scale(0.97);
        }

        &:focus {
            box-shadow: 0 0 0 2px rgba(rgb(219, 218, 218), 0.5);
            outline: none;
        }

        &.is-small {
            padding: 6px 10px;
            font-size: 0.75rem;
        }
    }

    .Button-Wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .IconBefore {
        font-size: 0.95rem;
        display: flex;
        justify-content: center;
        align-items: center;
        font-weight: normal;
    }

    .IconBefore + .Label:not(:empty) {
        margin-left: 5px;
    }

    .Label {
        font-size: 0.95rem;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &.Button--LoadMore {
        margin-top: 20px;
        width: 100%;
        display: block;
    }

    &.is-loading {
        position: relative;
        user-select: none;
        pointer-events: none;

        .Spinner {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }

        .Label {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-decoration: none;
            opacity: 0;
        }
    }
}
</style>
